import { Button, buttonVariants, IconButton } from './Button'
import {
  CalendarPresetIdSchema,
  createDefaultCalendarPresets,
} from './calendarPresets'
import type {
  CalendarPresetId,
  TCalendarPreset,
  TemporalDateRange,
} from './calendarPresets'
import { Input } from './Input'
import type { InputProps } from './Input'
import { itemVariants } from './Item'
import { Label } from './Label'
import { Separator } from './Separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './Select'
import { cn, tv, toDate, toPlainDate } from '@joyful/utils'
import {
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from 'lucide-react'
import { Temporal } from '@js-temporal/polyfill'
import * as React from 'react'
import { DayPicker, useDayPicker } from 'react-day-picker'
import type { DateRange, DayPickerProps, PropsRange } from 'react-day-picker'
import type { VariantProps } from '@joyful/utils'
import type { ComponentProps, Dispatch, ReactNode, SetStateAction } from 'react'
import { useRef, useState } from 'react'

function formatMonthYear(date: Date | undefined, fallback: string): string {
  return (
    toPlainDate(date)?.toLocaleString('en-US', {
      month: 'long',
      year: 'numeric',
    }) ?? fallback
  )
}

export type CalendarProps = DayPickerProps & {
  buttonNextClassName?: string

  buttonPreviousClassName?: string

  captionClassName?: string
  captionLabelClassName?: string
  dayButtonClassName?: string
  dayClassName?: string
  disabledClassName?: string
  hiddenClassName?: string
  monthCaptionClassName?: string
  monthClassName?: string
  monthGridClassName?: string
  monthsClassName?: string
  navClassName?: string
  outsideClassName?: string
  rangeEndClassName?: string
  rangeMiddleClassName?: string
  rangeStartClassName?: string
  selectedClassName?: string
  /**
   * Wether to show the year switcher in the caption.
   * @default true
   */
  showYearSwitcher?: boolean
  todayClassName?: string
  weekClassName?: string
  weekdayClassName?: string
  weekdaysClassName?: string
  /**
   * In the year view, the number of years to display at once.
   * @default 12
   */
  yearRange?: number
}

type NavView = 'days' | 'years'

/**
 * A custom calendar component built on top of react-day-picker.
 * @param props The props for the calendar.
 * @default yearRange 12
 * @returns
 */
export function Calendar({
  className,
  components,
  numberOfMonths,
  showOutsideDays = true,
  showYearSwitcher = true,
  yearRange = 12,
  ...props
}: CalendarProps) {
  const [navView, setNavView] = useState<NavView>('days')
  const [displayYears, setDisplayYears] = useState(() => {
    const currentYear = Temporal.Now.plainDateISO().year
    return {
      from: currentYear - Math.floor(yearRange / 2 - 1),
      to: currentYear + Math.ceil(yearRange / 2),
    }
  })

  const { endMonth, onNextClick, onPrevClick, startMonth } = props

  const columnsDisplayed = navView === 'years' ? 1 : numberOfMonths

  const buttonNavClassName = buttonVariants({
    className:
      'absolute size-7 bg-transparent p-0 opacity-50 hover:opacity-100',
    variant: 'outline',
  })
  const buttonRangeClassName =
    'bg-tremor-background-subtle [&>button]:bg-tremor-background-emphasis [&>button]:text-tremor-content-emphasis [&>button]:hover:bg-tremor-background-emphasis [&>button]:hover:text-tremor-content-emphasis'

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      style={{
        width: 248.8 * (columnsDisplayed ?? 1) + 'px',
      }}
      classNames={{
        button_next: cn(
          buttonNavClassName,
          'right-0',
          props.buttonNextClassName,
        ),
        button_previous: cn(
          buttonNavClassName,
          'left-0',
          props.buttonPreviousClassName,
        ),
        caption_label: cn(
          'truncate text-sm font-medium',
          props.captionLabelClassName,
        ),
        day: cn(
          'flex size-8 flex-1 items-center justify-center p-0 text-sm',
          props.dayClassName,
        ),
        day_button: cn(
          buttonVariants({
            variant: 'ghost',
          }).base(),
          'size-8 rounded-md p-0 font-normal transition-none aria-selected:opacity-100',
          props.dayButtonClassName,
        ),
        disabled: cn('text-tremor-content opacity-50', props.disabledClassName),
        hidden: cn('invisible flex-1', props.hiddenClassName),
        month: cn('w-full', props.monthClassName),
        month_caption: cn(
          'relative mx-10 flex items-center justify-center',
          props.captionClassName,
          props.monthCaptionClassName,
        ),
        month_grid: cn('mx-auto mt-4', props.monthGridClassName),
        months: cn('relative flex', props.monthsClassName),
        nav: cn('flex items-start', props.navClassName),
        outside: cn(
          'day-outside text-tremor-content aria-selected:bg-tremor-background-subtle/50 aria-selected:text-tremor-content opacity-50 aria-selected:opacity-30',
          props.outsideClassName,
        ),
        range_end: cn(
          buttonRangeClassName,
          'day-range-end rounded-e-md',
          props.rangeEndClassName,
        ),
        range_middle: cn(
          'bg-tremor-background-subtle text-tremor-content-emphasis! [&>button]:text-tremor-content-emphasis! [&>button]:hover:text-tremor-content-inverted! [&>button]:bg-transparent! [&>button]:hover:bg-transparent!',
          props.rangeMiddleClassName,
        ),
        range_start: cn(
          buttonRangeClassName,
          'day-range-start rounded-s-md',
          props.rangeStartClassName,
        ),
        selected: cn(
          '[&>button]:bg-tremor-background-emphasis [&>button]:text-tremor-content-inverted [&>button]:hover:bg-tremor-background-emphasis! [&>button]:hover:text-tremor-content-inverted',
          props.selectedClassName,
        ),
        today: cn(
          '[&>button]:bg-tremor-background-subtle [&>button]:text-tremor-content-emphasis! [&>button]:hover:bg-tremor-background-subtle!',
          props.todayClassName,
        ),
        week: cn('mt-2 flex w-max items-start', props.weekClassName),
        weekday: cn(
          'text-tremor-content w-8 text-sm font-normal',
          props.weekdayClassName,
        ),
        weekdays: cn('flex flex-row', props.weekdaysClassName),
      }}
      components={{
        CaptionLabel: (props) => {
          return (
            <CaptionLabel
              showYearSwitcher={showYearSwitcher}
              navView={navView}
              setNavView={setNavView}
              displayYears={displayYears}
              {...props}
            />
          )
        },

        Chevron: ({ orientation }) => {
          const Icon =
            orientation === 'left' ? ChevronLeftIcon : ChevronRightIcon
          return <Icon className="size-4" />
        },
        MonthGrid: ({ children, className, ...props }) => {
          return (
            <MonthGrid
              className={className}
              displayYears={displayYears}
              startMonth={startMonth}
              endMonth={endMonth}
              navView={navView}
              setNavView={setNavView}
              {...props}
            >
              {children}
            </MonthGrid>
          )
        },
        Nav: ({ className }) => {
          return (
            <Nav
              className={className}
              displayYears={displayYears}
              navView={navView}
              setDisplayYears={setDisplayYears}
              startMonth={startMonth}
              endMonth={endMonth}
              onPrevClick={onPrevClick}
              onNextClick={onNextClick}
            />
          )
        },
        ...components,
      }}
      numberOfMonths={columnsDisplayed}
      {...props}
    />
  )
}

function Nav({
  className,
  displayYears,
  endMonth,
  navView,
  onNextClick,
  onPrevClick,
  setDisplayYears,
  startMonth,
}: {
  className?: string
  displayYears: { from: number; to: number }
  endMonth?: Date
  navView: NavView
  onNextClick?: (date: Date) => void
  onPrevClick?: (date: Date) => void
  setDisplayYears: Dispatch<SetStateAction<{ from: number; to: number }>>
  startMonth?: Date
}) {
  const { goToMonth, nextMonth, previousMonth } = useDayPicker()

  const isPreviousDisabled = (() => {
    if (navView === 'years') {
      const yearDate = Temporal.PlainDate.from({
        day: 1,
        month: 1,
        year: displayYears.from - 1,
      })
      const startMonthPlainDate = toPlainDate(startMonth)
      const endMonthPlainDate = toPlainDate(endMonth)
      return (
        (startMonthPlainDate !== null &&
          Temporal.PlainDate.compare(yearDate, startMonthPlainDate) < 0) ??
        (endMonthPlainDate !== null &&
          Temporal.PlainDate.compare(yearDate, endMonthPlainDate) > 0)
      )
    }

    return previousMonth === undefined
  })()

  const isNextDisabled = (() => {
    if (navView === 'years') {
      const yearDate = Temporal.PlainDate.from({
        day: 1,
        month: 1,
        year: displayYears.to + 1,
      })
      const startMonthPlainDate = toPlainDate(startMonth)
      const endMonthPlainDate = toPlainDate(endMonth)
      return (
        (startMonthPlainDate !== null &&
          Temporal.PlainDate.compare(yearDate, startMonthPlainDate) < 0) ??
        (endMonthPlainDate !== null &&
          Temporal.PlainDate.compare(yearDate, endMonthPlainDate) > 0)
      )
    }

    return nextMonth === undefined
  })()

  const handlePreviousClick = () => {
    if (!previousMonth) {
      return
    }

    if (navView === 'years') {
      setDisplayYears((prev) => {
        return {
          from: prev.from - (prev.to - prev.from + 1),
          to: prev.to - (prev.to - prev.from + 1),
        }
      })
      onPrevClick?.(
        new Date(
          displayYears.from - (displayYears.to - displayYears.from),
          0,
          1,
        ),
      )
      return
    }

    goToMonth(previousMonth)
    onPrevClick?.(previousMonth)
  }

  const handleNextClick = () => {
    if (!nextMonth) {
      return
    }

    if (navView === 'years') {
      setDisplayYears((prev) => {
        return {
          from: prev.from + (prev.to - prev.from + 1),
          to: prev.to + (prev.to - prev.from + 1),
        }
      })
      onNextClick?.(
        new Date(
          displayYears.from + (displayYears.to - displayYears.from),
          0,
          1,
        ),
      )
      return
    }

    goToMonth(nextMonth)
    onNextClick?.(nextMonth)
  }

  return (
    <nav className={cn('flex items-center', className)}>
      <div className="absolute left-0">
        <IconButton
          variant="outline"
          size="sm"
          tabIndex={isPreviousDisabled ? undefined : -1}
          disabled={isPreviousDisabled}
          tooltip={{
            content:
              navView === 'years'
                ? `Previous ${displayYears.to - displayYears.from + 1} years`
                : formatMonthYear(previousMonth, 'Previous month'),
            tooltipRootProps: {
              delayDuration: 500,
            },
          }}
          onClick={handlePreviousClick}
        >
          <ChevronLeftIcon />
        </IconButton>
      </div>

      <div className="absolute right-0">
        <IconButton
          variant="outline"
          size="sm"
          tabIndex={isNextDisabled ? undefined : -1}
          disabled={isNextDisabled}
          tooltip={{
            content:
              navView === 'years'
                ? `Next ${displayYears.to - displayYears.from + 1} years`
                : formatMonthYear(nextMonth, 'Next month'),
            tooltipRootProps: {
              delayDuration: 500,
            },
          }}
          onClick={handleNextClick}
        >
          <ChevronRightIcon />
        </IconButton>
      </div>
    </nav>
  )
}

interface CaptionLabelProps extends ComponentProps<'span'> {
  displayYears: { from: number; to: number }
  navView: NavView
  setNavView: Dispatch<SetStateAction<NavView>>
  showYearSwitcher?: boolean
}

function CaptionLabel({
  children,
  displayYears,
  navView,
  setNavView,
  showYearSwitcher,
  ...props
}: CaptionLabelProps) {
  if (!showYearSwitcher) {
    return <span {...props}>{children}</span>
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      weight="medium"
      width="full"
      onClick={() => {
        return setNavView((prev) => {
          return prev === 'days' ? 'years' : 'days'
        })
      }}
    >
      <span className="truncate">
        {navView === 'days'
          ? children
          : displayYears.from + ' - ' + displayYears.to}
      </span>
    </Button>
  )
}

function MonthGrid({
  children,
  className,
  displayYears,
  endMonth,
  navView,
  setNavView,
  startMonth,
  ...props
}: {
  children: ReactNode
  className?: string
  displayYears: { from: number; to: number }
  endMonth?: Date
  navView: NavView
  setNavView: Dispatch<SetStateAction<NavView>>
  startMonth?: Date
} & ComponentProps<'table'>) {
  if (navView === 'years') {
    return (
      <YearGrid
        displayYears={displayYears}
        startMonth={startMonth}
        endMonth={endMonth}
        setNavView={setNavView}
        navView={navView}
        className={className}
        {...props}
      />
    )
  }

  return (
    <table className={className} {...props}>
      {children}
    </table>
  )
}

function YearGrid({
  className,
  displayYears,
  endMonth,
  navView,
  setNavView,
  startMonth,
  ...props
}: {
  className?: string
  displayYears: { from: number; to: number }
  endMonth?: Date
  navView: NavView
  setNavView: Dispatch<SetStateAction<NavView>>
  startMonth?: Date
} & ComponentProps<'div'>) {
  const { goToMonth, selected } = useDayPicker<{
    mode: 'range'
    required?: boolean
  }>()

  return (
    <div className={cn('grid grid-cols-4 gap-1', className)} {...props}>
      {Array.from({ length: displayYears.to - displayYears.from + 1 }).map(
        (_, index) => {
          const yearEndDate = Temporal.PlainDate.from({
            day: 31,
            month: 12,
            year: displayYears.from + index,
          })
          const yearStartDate = Temporal.PlainDate.from({
            day: 1,
            month: 1,
            year: displayYears.from + index,
          })

          const startMonthPlainDate = toPlainDate(startMonth)
          const endMonthPlainDate = toPlainDate(endMonth)
          const isBefore =
            startMonthPlainDate !== null &&
            Temporal.PlainDate.compare(yearEndDate, startMonthPlainDate) < 0

          const isAfter =
            endMonthPlainDate !== null &&
            Temporal.PlainDate.compare(yearStartDate, endMonthPlainDate) > 0

          const isDisabled = isBefore || isAfter
          const currentYear = Temporal.Now.plainDateISO().year
          const year = displayYears.from + index
          const isCurrentYear = year === currentYear

          return (
            <div
              key={year}
              className={cn(
                isCurrentYear &&
                  '**:data-[slot=button]:bg-tremor-background-subtle **:data-[slot=button]:text-tremor-content-emphasis **:data-[slot=button]:hover:not-disabled:bg-tremor-background-subtle',
              )}
            >
              <Button
                variant="ghost"
                weight={isCurrentYear ? 'medium' : 'normal'}
                onClick={() => {
                  setNavView('days')
                  goToMonth(new Date(year, selected?.from?.getMonth() ?? 0))
                }}
                disabled={navView === 'years' ? isDisabled : undefined}
              >
                {year}
              </Button>
            </div>
          )
        },
      )}
    </div>
  )
}

const presetClassNames = itemVariants({
  isInteractive: true,
  size: 'lg',
})

function isPresetSelected({
  dateRange,
  preset,
}: {
  dateRange: TemporalDateRange
  preset: TCalendarPreset
}) {
  return (
    dateRange.from !== null &&
    dateRange.to !== null &&
    dateRange.from.equals(preset.value.from) &&
    dateRange.to.equals(preset.value.to)
  )
}

interface CalendarRangePresetsProps {
  dateRange: TemporalDateRange
  onSelect: (preset: TCalendarPreset) => void
  selectedPresetId?: CalendarPresetId | null
}

function CalendarRangePresetColumn({
  dateRange,
  onSelect,
  selectedPresetId,
}: CalendarRangePresetsProps) {
  return (
    <div className="flex min-w-40 flex-col p-2">
      {createDefaultCalendarPresets().map((preset) => {
        const isSelected =
          selectedPresetId === preset.id ||
          (!selectedPresetId && isPresetSelected({ dateRange, preset }))
        return (
          <button
            key={preset.label}
            type="button"
            className={presetClassNames.base()}
            onClick={() => {
              onSelect(preset)
            }}
          >
            <div className={presetClassNames.container()}>{preset.label}</div>
            {isSelected ? <CheckIcon className="size-4 stroke-2" /> : null}
          </button>
        )
      })}
    </div>
  )
}

function CalendarRangePresetSelect({
  dateRange,
  onSelect,
  selectedPresetId,
}: CalendarRangePresetsProps) {
  const presets = createDefaultCalendarPresets()
  const selectedPreset = !selectedPresetId
    ? presets.find((preset) => {
        return isPresetSelected({ dateRange, preset })
      })
    : presets.find((preset) => {
        return preset.id === selectedPresetId
      })

  return (
    <Select
      value={selectedPreset?.id}
      onValueChange={(value) => {
        const parsedPresetId = CalendarPresetIdSchema.safeParse(value)
        if (!parsedPresetId.success) {
          return
        }

        const nextPreset = presets.find((preset) => {
          return preset.id === parsedPresetId.data
        })
        if (nextPreset) {
          onSelect(nextPreset)
        }
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder="Select a date range…" />
      </SelectTrigger>

      <SelectContent>
        {presets.map((preset) => {
          return (
            <SelectItem key={preset.id} value={preset.id}>
              {preset.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

const calendarRangePickerVariants = tv({
  base: 'flex',

  variants: {
    presetVariant: {
      column: null,
      select: 'flex-col gap-1 py-1',
    },
  },
})

export interface CalendarRangePickerProps
  extends
    Omit<PropsRange, 'mode' | 'required' | 'selected' | 'onSelect'>,
    VariantProps<typeof calendarRangePickerVariants> {
  className?: string
  dateRange: TemporalDateRange
  onEnterCommit?: () => void
  onSelectDateRange: (range: TemporalDateRange) => void
  onSelectFromPresets?: () => void
  onSelectPreset?: (presetId: CalendarPresetId) => void
  /**
   * When false, either bound may be empty.
   * @default true
   */
  required?: boolean
  selectedPresetId?: CalendarPresetId | null
}

export function CalendarRangePicker({
  className,
  dateRange,
  onEnterCommit,
  onSelectDateRange,
  onSelectFromPresets,
  onSelectPreset,
  presetVariant,
  required = true,
  selectedPresetId,
  ...calendarProps
}: CalendarRangePickerProps) {
  const presetProps: CalendarRangePresetsProps = {
    dateRange,
    onSelect: (preset) => {
      if (onSelectPreset) {
        onSelectPreset(preset.id)
      } else {
        onSelectDateRange(preset.value)
      }

      onSelectFromPresets?.()
    },
    selectedPresetId,
  }
  return (
    <div
      className={cn(
        calendarRangePickerVariants({
          presetVariant,
        }),
        className,
      )}
    >
      {presetVariant === 'select' ? (
        <CalendarRangePresetSelect {...presetProps} />
      ) : (
        <CalendarRangePresetColumn {...presetProps} />
      )}

      {presetVariant === 'select' ? null : <Separator variant="vertical" />}

      <div className="flex flex-col items-center">
        <fieldset className="grid grid-cols-2 gap-x-2 p-3">
          <DayPickerInput
            allowEmpty={!required}
            id="start"
            label="Start"
            onChange={(value) => {
              onSelectDateRange({
                from: value,
                to: dateRange.to,
              })
            }}
            onEnterCommit={onEnterCommit}
            value={dateRange.from}
          />

          <DayPickerInput
            allowEmpty={!required}
            id="end"
            label="End"
            value={dateRange.to}
            onChange={(value) => {
              onSelectDateRange({
                from: dateRange.from,
                to: value,
              })
            }}
            onEnterCommit={onEnterCommit}
          />
        </fieldset>

        <Separator />

        <Calendar
          mode="range"
          required={required}
          defaultMonth={toDate(dateRange.from) ?? undefined}
          selected={{
            from: toDate(dateRange.from) ?? undefined,
            to: toDate(dateRange.to) ?? undefined,
          }}
          onSelect={(range: DateRange | undefined) => {
            onSelectDateRange({
              from: toPlainDate(range?.from),
              to: toPlainDate(range?.to),
            })
          }}
          {...calendarProps}
        />
      </div>
    </div>
  )
}

interface DayPickerInputProps extends Omit<InputProps, 'value' | 'onChange'> {
  allowEmpty?: boolean
  id: string
  label?: string
  onChange: (day: Temporal.PlainDate | null) => void
  onEnterCommit?: () => void
  value: Temporal.PlainDate | null
}

export function DayPickerInput({
  allowEmpty = false,
  className,
  id,
  label,
  onChange,
  onEnterCommit,
  value,
  ...props
}: DayPickerInputProps) {
  const propValue = value?.toString()
  const [localValue, setLocalValue] = useState(propValue)
  const [isFocused, setFocused] = useState(false)
  const suppressNextBlurCommitRef = useRef(false)

  const { onKeyDown: onKeyDownFromProps, ...inputRestProps } = props

  const displayValue = isFocused ? (localValue ?? '') : (propValue ?? '')

  const applyCommittedDraft = (element: HTMLInputElement) => {
    const newValue = element.value.trim()
    setFocused(false)
    if (newValue !== propValue) {
      onChange(toPlainDate(newValue))
    }
  }

  return (
    <div className={cn('flex flex-col gap-y-1', className)}>
      {label === undefined ? null : (
        <Label htmlFor={id} size="sm">
          {label}
        </Label>
      )}
      <Input
        id={id}
        inputClassName={cn(
          'uppercase [&::-webkit-calendar-picker-indicator]:hidden',
        )}
        fontVariant="tabular"
        type="date"
        size="sm"
        placeholder="MM/DD/YYYY"
        value={displayValue}
        {...inputRestProps}
        endAdornment={
          allowEmpty && value !== null ? (
            <IconButton
              size="xs"
              variant="ghost"
              tooltip={label ? `Clear ${label}` : 'Clear'}
              onMouseDown={(event) => {
                event.preventDefault()
              }}
              onClick={() => {
                setLocalValue('')
                onChange(null)
              }}
            >
              <XIcon />
            </IconButton>
          ) : undefined
        }
        onFocus={() => {
          setFocused(true)
          setLocalValue(propValue)
        }}
        onChange={(event) => {
          setLocalValue(event.target.value.trim())
        }}
        onBlur={(event) => {
          if (suppressNextBlurCommitRef.current) {
            suppressNextBlurCommitRef.current = false
            return
          }

          applyCommittedDraft(event.currentTarget)
        }}
        onKeyDown={(event) => {
          onKeyDownFromProps?.(event)
          if (event.defaultPrevented) {
            return
          }

          if (event.key !== 'Enter') {
            return
          }

          event.preventDefault()
          if (onEnterCommit) {
            applyCommittedDraft(event.currentTarget)
            onEnterCommit()
            return
          }

          suppressNextBlurCommitRef.current = true
          applyCommittedDraft(event.currentTarget)
          event.currentTarget.blur()
        }}
      />
    </div>
  )
}
